import React, {RefObject, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import classNames from 'classnames';
import {CoreDoms, ViewMode, EditorExperienceType} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import {PagesSelector} from '../../../Selectors';
import Page from '../../Page/Page';
import {useExperience} from '../../Experience/ExperienceProvider';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import PageToolbar from '../../Page/PageToolbar';
import PageName from '../../Page/PageName';
import {PageActionsProvider} from '../../Context/PageActions/PageActionsContext';

export interface ListViewProps {
  layout: ProductLayout;
  containerRef: RefObject<HTMLElement>;
}

const ListView = ({layout}: ListViewProps) => {
  const ui = useUiState();
  const {experience} = useExperience();

  // get the pages from the store
  const pages = useSelector(PagesSelector);
  const pageListItems = pages.length;

  const editorType = useMemo(() => {
    return ui.state.canEditPageLayout ? EditorExperienceType.Normal : EditorExperienceType.Locked;
  }, [ui.state.canEditPageLayout]);

  const workAreaToolbarButtons = [
    experience.EDITOR.workArea.general.undoRedoButtons.undoRedoSmall,
    experience.EDITOR.workArea.general.viewModeSelector.viewModeSelectorSmall,
    experience.EDITOR.pageEditing.allowUsersToToggleLocking,
  ].filter(Boolean).length;

  // Classes for the UL
  const pagesContainerClasses = classNames(
    // Transition the margin-top when it is changed for focus mode
    'transition-all',
    'duration-150',
    'delay-300',
    // In List view, position the first page based on whether there are any work area toolbar buttons or not
    // otherwise it will just have a small margin so that it sits at the top of the work area
    ui.state.viewMode === ViewMode.List &&
      (!ui.state.isSmallScreen ||
        (ui.state.isSmallScreen && (ui.state.focusMode || workAreaToolbarButtons > 0)))
      ? 'mt-[500rem]'
      : 'mt-[50rem]',
    'after:h-[500rem] after:w-full after:shrink-0'
  );

  const tpxCoreAttrs = {
    [CoreDoms.ProjectPagesAttr]: 'true',
  };

  const listItemClasses = classNames([
    'flex',
    'flex-col',
    pageListItems > 1 ? 'lg:snap-center mb-[40rem]' : 'single-page',
  ]);

  return (
    <ul {...tpxCoreAttrs} className={pagesContainerClasses} key={editorType}>
      {pages.map((page: PageInterface) => {
        const selectedPageStyles = classNames(
          // When the page options palette is open, highlight the current page
          ui.state.palette.section?.includes('pageActionsPalette') &&
            ui.state.pageNumber === page.pagenumber &&
            'outline outline-themeAccentColour'
        );
        return (
          <li data-id={page.pagenumber} className={listItemClasses} key={`listview-${page.ref}`}>
            <Page
              page={page}
              layout={layout}
              editorType={editorType}
              viewMode={ViewMode.List}
              showErrors={true}
              showEmptyBoxes={true}
              className={selectedPageStyles}
            >
              <PageName page={page} layout={layout} />
              {experience.EDITOR.workArea.listView.pageActionButtons && (
                <PageActionsProvider>
                  <PageToolbar pageNumber={page.pagenumber} />
                </PageActionsProvider>
              )}
            </Page>
          </li>
        );
      })}
    </ul>
  );
};

export default ListView;
